/**
 * FloorPlanView (PRP-SKETCH-FIELD-1 work item 7): a read-only plan of the
 * rooms a project's field notes carry geometry for. All geometry comes from the
 * shared sketch core (`@sketch-core`, the same code as the phone Sketch screen).
 *
 * PRP-SKETCH-FIELD-3 J15: drawn from the plan wall graph (`buildWallGraph`) in
 * `DRAW_ORDER`: room fills (with each wall's room-side half), then every wall
 * segment once on its centerline (a T junction is one straight line, H13), then
 * every opening once (`openingGlyph`: door kinds per door_glyph.py; a door
 * swinging into a later room is never painted over, H12), then the J19 labels
 * (`wallLabels`). A fully missing (0 in) wall is a light dotted line with no gap.
 * A wall thicker than the interior default keeps E19's thin face line(s). No
 * editing. v1 documents go through anyToV2 inside the core, and the converter's
 * banner is shown as a small note. Colours are the V2 tokens, so light and dark
 * follow the app theme.
 *
 * PRP-SKETCH-FIELD-5 item 6 (read only): damage marked on the phone, from the room's checklist
 * (`room_checklist`: damagedAreas, wallSpans, waterLineIn): each damaged wall stretch in the damage
 * colour over the wall, the water line as a dashed line inside it with its "WL 24 in" label, and CEIL /
 * FLOOR pills under the room name. Colours, sizes and wording are the shared policy's `damage` block.
 *
 * PRP-WEB-1 W3.2: the SVG body lives in `plan/PlanCanvas.tsx` (shared with the full-screen plan
 * page); this component is the thin read-only wrapper. Props and output are unchanged.
 */
import { useMemo } from 'react';
import {
  planFromStoredRooms, DAMAGE_AREAS, resolveSpan,
  type DamageMarks, type Room, type StoredRoom, type RoomGeometryV1, type RoomGeometryV2, type Plan,
} from '@sketch-core';
import type { FieldNote } from '../../types';
import { PlanCanvas } from './plan/PlanCanvas';

/** Gap between the v2 plan and the v1 rooms, which carry no position (feet). */
const V1_GAP_FT = 3;
export interface FloorPlanData { plan: Plan; banners: { room: string; text: string }[] }

type WithChecklist = FieldNote & { room_checklist?: unknown };
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Spans are positions along a wall, so they are clipped to the wall's length as drawn now (an older
 * phone app can move a wall without knowing the spans); a span whose wall is gone is dropped.
 */
function clipSpans(room: Room, marks: DamageMarks): DamageMarks {
  if (!marks.wallSpans) return marks;
  const kept = marks.wallSpans.flatMap((sp) => {
    const res = resolveSpan(room, sp);
    if (!res) return [];
    const lo = Math.max(0, Math.min(res.fromFt, res.toFt)), hi = Math.min(res.lengthFt, Math.max(res.fromFt, res.toFt));
    if (hi - lo <= 1e-6) return [];
    const mirrored = Math.abs(res.fromFt - sp.fromFt) > 1e-9 || Math.abs(res.toFt - sp.toFt) > 1e-9;
    return [mirrored ? { ...sp, fromFt: res.lengthFt - hi, toFt: res.lengthFt - lo } : { ...sp, fromFt: lo, toFt: hi }];
  });
  // Every span gone: nothing of the walls is marked (an empty list would read as "every wall").
  if (!kept.length) return { ...marks, wallSpans: undefined, damagedAreas: marks.damagedAreas.filter((a) => a !== DAMAGE_AREAS.walls) };
  return { ...marks, wallSpans: kept };
}

/** The damage half of a room's checklist as the sketch core reads it; malformed entries are left out. */
export function damageMarksOf(checklist: unknown): DamageMarks | null {
  if (!checklist || typeof checklist !== 'object') return null;
  const c = checklist as Record<string, unknown>;
  const areas = Array.isArray(c.damagedAreas) ? c.damagedAreas.filter((a): a is string => typeof a === 'string') : [];
  const spans = Array.isArray(c.wallSpans)
    ? c.wallSpans.flatMap((x) => {
      const t = x as Record<string, unknown> | null;
      if (!t || typeof t !== 'object' || !finite(t.fromFt) || !finite(t.toFt)) return [];
      const ok = (v: unknown) => typeof v === 'string' || finite(v);
      return ok(t.a) && ok(t.b) ? [{ a: String(t.a), b: String(t.b), fromFt: t.fromFt, toFt: t.toFt }] : [];
    })
    : [];
  const marks: DamageMarks = { damagedAreas: areas };
  if (spans.length) marks.wallSpans = spans;
  if (finite(c.waterLineIn) && c.waterLineIn >= 0) marks.waterLineIn = c.waterLineIn;
  if (!marks.damagedAreas.length && !spans.length && marks.waterLineIn == null) return null;
  return marks;
}

/**
 * The newest geometry per room_id (a room is the notes sharing a room_id), as a
 * core plan. v1 rooms have no position, so they are laid out in a row to the
 * right of the placed (v2) rooms instead of stacking on the origin.
 */
export function planFromNotes(notes: FieldNote[]): FloorPlanData | null {
  const newest = new Map<string, FieldNote>();
  for (const n of notes) {
    if (!n.room_geometry) continue;
    const key = n.room_id || n.id;
    const cur = newest.get(key);
    if (!cur || (n.created_at ?? '') >= (cur.created_at ?? '')) newest.set(key, n);
  }
  const v2: StoredRoom[] = [];
  const v1: StoredRoom[] = [];
  const marksOfRoom = new Map<string, DamageMarks>();
  newest.forEach((n, key) => {
    const g = n.room_geometry as unknown as RoomGeometryV1 | RoomGeometryV2;
    const s: StoredRoom = { id: key, name: n.room_type ?? (g as RoomGeometryV2).name ?? undefined, geometry: g };
    const marks = damageMarksOf((n as WithChecklist).room_checklist);
    if (marks) marksOfRoom.set(key, marks);
    ((g as RoomGeometryV2).version === 2 ? v2 : v1).push(s);
  });
  if (!v2.length && !v1.length) return null;
  const { plan } = planFromStoredRooms([...v2, ...v1]);
  for (const r of plan.rooms) { const m = marksOfRoom.get(r.id); if (m) r.damage = clipSpans(r, m); }
  const v1Ids = new Set(v1.map((s) => s.id));
  let x = 0;
  const placed = plan.rooms.filter((r) => !v1Ids.has(r.id));
  if (placed.length) x = Math.max(...placed.flatMap((r) => r.corners.map((c) => c.x))) + V1_GAP_FT;
  for (const r of plan.rooms) {
    if (!v1Ids.has(r.id) || r.corners.length < 3) continue;
    const minX = Math.min(...r.corners.map((c) => c.x));
    const maxX = Math.max(...r.corners.map((c) => c.x));
    const dx = x - minX;
    for (const c of r.corners) c.x += dx;
    x += maxX - minX + V1_GAP_FT;
  }
  plan.rooms = plan.rooms.filter((r) => r.corners.length >= 3);
  if (!plan.rooms.length) return null;
  const banners = plan.rooms.filter((r) => r.banner).map((r) => ({ room: r.name, text: r.banner as string }));
  return { plan, banners };
}

export function FloorPlanView({ notes, className }: { notes: FieldNote[]; className?: string }) {
  const data = useMemo(() => planFromNotes(notes), [notes]);
  if (!data) return null;
  const { plan, banners } = data;
  return (
    <section className={className} data-testid="floor-plan-view" aria-label="Floor plan">
      <div className="mb-2 text-[13px] font-[620] text-[var(--tx-hi)]">
        Floor plan <span className="font-normal text-[var(--tx-faint)]">({plan.rooms.length} {plan.rooms.length === 1 ? 'room' : 'rooms'}, read only)</span>
      </div>
      <div className="rounded-lg border border-[var(--bd)] bg-[var(--bg-1)] p-2">
        <PlanCanvas plan={plan} className="block h-auto w-full" style={{ maxHeight: 520 }} />
      </div>
      {banners.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[11.5px] text-[var(--tx-faint)]" data-testid="plan-banners">
          {banners.map((x, i) => (<li key={i}>{x.room}: {x.text}</li>))}
        </ul>
      )}
    </section>
  );
}

export default FloorPlanView;
